import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { IAuthTokens } from '../stores/authTokens/authTokenTypes';
import { useQuery } from '@tanstack/react-query';
import { isTokenError } from '../stores/authTokens/isTokenError';
import { meQueryOptions } from '../../ui/hooks/meQuery';
import type Agent from '../agent';

/** How long the profile is trusted before a component mount refetches it. */
const PROFILE_STALE_MS = 60 * 1000;

/**
 * The logged-in user's token and profile.
 *
 * OKTA-10/12 (BCD-1755, BCD-1757): the token is treated as an opaque credential. The only thing
 * read from it is `sub` (the user's email), which every token type carries — a Bruntwood token or a
 * provider (Okta) access token alike. Everything else about the user (enhanced, the gym waiver,
 * the hide-waiver flag) comes from companion-auth's GET /api/user/me, i.e. live data rather than a
 * snapshot baked into a token that may be weeks old.
 *
 * `loading` stays true until the profile has arrived, so callers that gate on it (e.g. the enhanced
 * route) never act on a half-loaded user. `profileError` is true when the profile could not be
 * loaded at all, so a failed lookup is never mistaken for a non-enhanced user. `refetch` refreshes
 * both the token and the profile.
 *
 * Must always be used within an observer component
 */
export const useBWTokenContext = () => {
  const context = useContext(MobXProviderContext);
  if (context === undefined) {
    throw new Error(
      'useBWTokenContext must be used within a MobXProviderContext',
    );
  }

  const authTokens = context.authTokens as IAuthTokens;
  const agent = (context.authStore as { agent?: Agent } | undefined)?.agent;

  const bruntwoodTokenQuery = useQuery({
    queryKey: ['bruntwoodToken'],
    queryFn: async () => {
      const result = await authTokens.getAccessToken('bruntwoodToken', true);
      if (isTokenError(result)) {
        throw new Error(result.message);
      } else {
        return result;
      }
    },
    initialData: authTokens.lastTokenValues.bruntwoodToken,
  });

  // Identity only: `sub` is present on every token type.
  const email = authTokens.getSourceMeta<{ sub?: string }>(
    'bruntwoodToken',
  )?.sub;

  const profileQuery = useQuery({
    ...meQueryOptions(agent as Agent, email),
    enabled: !!email && !!agent,
    staleTime: PROFILE_STALE_MS,
  });
  const profile = profileQuery.data?.profile;

  return {
    token: bruntwoodTokenQuery.data,
    loading:
      bruntwoodTokenQuery.isLoading || (!!email && profileQuery.isLoading),
    profileError: !profile && profileQuery.isError,
    isEnhancedUser: profile?.flags?.enhanced,
    email,
    gymWaiverStatus: profile?.gymWaiver,
    gymWaiverExpiryDate: profile?.gymWaiverExpiryDate,
    hideExpiredMessage: profile?.flags?.hideGymWaiverMessage,
    refetch: async () => {
      const token = await bruntwoodTokenQuery.refetch();
      await profileQuery.refetch();
      return token;
    },
  };
};
